/**
 * The recruiter service returns errors in two families of shapes.
 *
 * Newer routes wrap everything in an `error` envelope:
 *
 *   { "error": { "code": "VALIDATION_ERROR", "message": "…" } }
 *
 * Older ones still use FastAPI's `detail`, itself in three shapes depending on
 * where the error was raised:
 *
 *   { "detail": { "code": "OFFER_ALREADY_EXISTS", "message": "…" } }  ← raised by us
 *   { "detail": "Some plain message" }                                ← HTTPException
 *   { "detail": [ { "msg": "…", "loc": [...] } ] }                    ← 422 validation
 *
 * Reading either straight into a toast renders "[object Object]" or nothing at
 * all for most of those, so always go through these helpers.
 */

type ApiErrorDetail =
  | string
  | { code?: string; message?: string }
  | Array<{ msg?: string }>;

// Inside the `error` envelope a 422 arrives as a stringified python list, e.g.
// [{'type': 'literal_error', 'loc': ('domain',), 'msg': "Input should be 'Tech', …", …}]
const PYDANTIC_MSG = /'msg':\s*(?:"([^"]*)"|'([^']*)')/;
const PYDANTIC_LOC = /'loc':\s*\(([^)]*)\)/;

/** Turns a pydantic error repr into `<field>: <msg>`, or null if it isn't one. */
const humanizePydanticError = (raw: string): string | null => {
  const msgMatch = raw.match(PYDANTIC_MSG);
  if (!msgMatch) return null;

  const msg = msgMatch[1] ?? msgMatch[2] ?? "";
  const field = raw
    .match(PYDANTIC_LOC)?.[1]
    ?.split(",")
    .map((part) => part.trim().replace(/^['"]|['"]$/g, ""))
    .filter(Boolean)
    .pop();

  return field ? `${field}: ${msg}` : msg;
};

/** The backend's machine-readable error code, when it sent one. */
export const getApiErrorCode = (error: any): string | undefined => {
  const data = error?.response?.data;

  if (typeof data?.error?.code === "string") return data.error.code;

  const detail = data?.detail as ApiErrorDetail | undefined;
  if (detail && !Array.isArray(detail) && typeof detail === "object") {
    return detail.code;
  }
  return undefined;
};

/** A message safe to hand to a toast, falling back when nothing usable came back. */
export const getApiErrorMessage = (error: any, fallback: string): string => {
  const data = error?.response?.data;

  const enveloped = data?.error?.message;
  if (typeof enveloped === "string" && enveloped.trim()) {
    return humanizePydanticError(enveloped) || enveloped;
  }

  const detail = data?.detail as ApiErrorDetail | undefined;

  if (typeof detail === "string" && detail.trim()) return detail;

  if (Array.isArray(detail)) {
    const msg = detail.find((d) => d?.msg)?.msg;
    if (msg) return msg;
  } else if (detail && typeof detail === "object" && detail.message) {
    return detail.message;
  }

  if (typeof data?.message === "string" && data.message.trim()) {
    return data.message;
  }

  return fallback;
};
